import { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, ArrowClockwise, Compass } from "@phosphor-icons/react";
import { Reveal, Eyebrow } from "./Reveal";
import { QUIZ, RESULTS, FLAG_NOTE } from "../data/content";
import { scrollToId } from "../lib/api";

const EASE = [0.16, 1, 0.3, 1];

export default function Quiz() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState([]);
  const done = answers.length === QUIZ.length;

  const result = useMemo(() => {
    if (!done) return null;
    const totals = { buy: 0, hold: 0, sell: 0, exit: 0 };
    answers.forEach((a) => Object.entries(a.scores).forEach(([k, v]) => (totals[k] += v)));
    const priority = ["exit", "sell", "buy", "hold"];
    const key = priority.reduce((best, k) => (totals[k] > totals[best] ? k : best), "hold");
    return { key, ...RESULTS[key], flagged: answers.some((a) => a.flag === "upfront") };
  }, [answers, done]);

  const choose = (option) => {
    setAnswers((prev) => [...prev.slice(0, step), option]);
    setStep((s) => Math.min(s + 1, QUIZ.length));
  };

  const back = () => {
    if (done || step > 0) {
      setAnswers((prev) => prev.slice(0, Math.max(0, step - (done ? 1 : 1))));
      setStep((s) => Math.max(0, s - 1));
    }
  };

  const restart = () => {
    setAnswers([]);
    setStep(0);
  };

  const progress = Math.round((answers.length / QUIZ.length) * 100);
  const q = QUIZ[step];

  return (
    <section id="decision-tool" data-testid="quiz-section" className="py-24 md:py-40 bg-secondary/40 border-y border-border">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12">
        <Reveal className="lg:col-span-4">
          <Eyebrow>The Decision Tool</Eyebrow>
          <h2 className="font-serif text-primary tracking-tight text-4xl sm:text-5xl mt-6 leading-[1.08]" data-testid="quiz-heading">
            Should you buy more — or sell?
          </h2>
          <p className="mt-6 text-foreground/70 max-w-sm">
            Six honest questions. One clear, personalised direction — plus the chapters worth reading first.
          </p>
          <div className="mt-10 hidden lg:flex items-center gap-3 text-foreground/50">
            <Compass size={40} weight="duotone" className="text-accent" />
            <p className="text-xs font-semibold uppercase tracking-[0.2em]">60 seconds, no email required</p>
          </div>
        </Reveal>

        <Reveal delay={0.15} className="lg:col-span-8">
          <div className="bg-card border border-border rounded-sm p-8 md:p-14 relative overflow-hidden" data-testid="quiz-card">
            <div className="absolute top-0 left-0 right-0 h-1 bg-muted">
              <motion.div
                className="h-full bg-accent"
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.6, ease: EASE }}
                data-testid="quiz-progress-bar"
              />
            </div>
            <AnimatePresence mode="wait">
              {!done && q ? (
                <motion.div
                  key={step}
                  initial={{ opacity: 0, x: 40 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -40 }}
                  transition={{ duration: 0.45, ease: EASE }}
                >
                  <p className="text-xs font-bold uppercase tracking-[0.25em] text-muted-foreground" data-testid="quiz-step-count">
                    Question {step + 1} of {QUIZ.length}
                  </p>
                  <h3 className="font-serif text-primary text-2xl md:text-3xl mt-4 tracking-tight" data-testid="quiz-question">
                    {q.question}
                  </h3>
                  <div className="mt-8 space-y-3">
                    {q.options.map((opt, i) => (
                      <motion.button
                        key={i}
                        data-testid={`quiz-option-${step}-${i}`}
                        onClick={() => choose(opt)}
                        whileHover={{ x: 6 }}
                        whileTap={{ scale: 0.985 }}
                        className="w-full text-left flex items-center justify-between gap-4 border border-border rounded-sm px-6 py-4 font-medium text-foreground/80 hover:border-accent hover:bg-secondary/50 transition-colors duration-300 group"
                      >
                        {opt.label}
                        <ArrowRight size={18} weight="bold" className="text-accent opacity-0 group-hover:opacity-100 transition-opacity duration-300 shrink-0" />
                      </motion.button>
                    ))}
                  </div>
                  {step > 0 && (
                    <button
                      data-testid="quiz-back-button"
                      onClick={back}
                      className="mt-8 flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-primary transition-colors duration-300"
                    >
                      <ArrowLeft size={16} weight="bold" /> Back
                    </button>
                  )}
                </motion.div>
              ) : result ? (
                <motion.div
                  key="result"
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7, ease: EASE }}
                  data-testid="quiz-result"
                >
                  <p className="text-xs font-bold uppercase tracking-[0.25em] text-accent">Your direction</p>
                  <h3 className="font-serif text-primary text-3xl md:text-4xl mt-4 tracking-tight leading-[1.12]" data-testid="quiz-result-title">
                    {result.title}
                  </h3>
                  <p className="mt-5 text-foreground/70 max-w-xl">{result.summary}</p>
                  <ul className="mt-6 space-y-3 max-w-xl">
                    {result.bullets.map((b, i) => (
                      <li key={i} className="flex gap-3 text-sm md:text-base text-foreground/80">
                        <span className="mt-[9px] h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                        {b}
                      </li>
                    ))}
                  </ul>
                  {result.flagged && (
                    <div className="mt-8 border-l-2 border-accent bg-secondary/60 px-6 py-5 text-sm text-foreground/80 max-w-xl" data-testid="quiz-flag-note">
                      {FLAG_NOTE}
                    </div>
                  )}
                  <div className="mt-10 flex flex-wrap gap-4">
                    <button
                      data-testid="quiz-cta-consult"
                      onClick={() => scrollToId("consult")}
                      className="group flex items-center gap-3 bg-primary text-primary-foreground px-7 py-3.5 rounded-full font-semibold text-sm hover:bg-accent transition-colors duration-300"
                    >
                      Talk it through with us
                      <ArrowRight size={16} weight="bold" className="transition-transform duration-300 group-hover:translate-x-1" />
                    </button>
                    <button
                      data-testid="quiz-restart-button"
                      onClick={restart}
                      className="flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold text-sm border border-border text-foreground/70 hover:border-primary hover:text-primary transition-colors duration-300"
                    >
                      <ArrowClockwise size={16} weight="bold" /> Start over
                    </button>
                  </div>
                </motion.div>
              ) : null}
            </AnimatePresence>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
